import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {toast} from 'react-toastify';
import {useTranslation} from 'react-i18next';
import {AxiosError} from 'axios';
import {QUERY_KEYS} from '../../../utils/queryKeys';
import {transformZodErrors} from '../../../incase-domain/validators/transformZodErrors';
import {updateUserValidator} from '../../../incase-domain/validators/user/updateUser';
import {User, UserConfig} from '../../../incase-domain/user';
import {loggedInUserSelector} from '../../../store/selectors/auth.selectors';
import {fetchUserConfig} from '../../../service/userConfig.service';
import {fetchDefaultUserConfigValues, fetchUser} from '../../../service/authorization';
import {UserForm, UserFormType} from './UserForm';
import Modal from '../../../incase-domain/components/modals/directory/Modal';
import {type TypedTranslation} from '../../../i18n';
import {useUpdateUserMutation} from './utils/queries';

const EDIT_USER_FORM_ID = 'edit_user_form';

interface EditUserModalProps {
    isOpen: boolean;
    onCancel: () => void;
    userId: string;
}

export function EditUserModal({isOpen, onCancel, userId}: EditUserModalProps): JSX.Element {
    const {t} = useTranslation();

    return (
        <Modal
            isOpen={isOpen}
            header={t('registerPage.updateUser')}
            showCancel
            cancelText={t('common.dismiss')}
            onCancel={onCancel}
            showConfirm
            confirmText={t('registerPage.update')}
            formId={EDIT_USER_FORM_ID}
            size="md"
            dataCy="edit_user_modal"
        >
            <EditUserForm userId={userId} onClose={onCancel} />
        </Modal>
    );
}

function EditUserForm({userId, onClose}: {userId: string; onClose: () => void}): JSX.Element {
    const client = useQueryClient();
    const {t} = useTranslation();

    const loggedInUser = useSelector(loggedInUserSelector);

    const {data: selectedUser, isLoading: isLoadingUser} = useQuery(QUERY_KEYS.user.detail(userId), () => fetchUser(userId), {
        onError: (err: AxiosError) => {
            if (err.response?.status === 400) onClose();
        },
    });

    const {data: userConfig} = useQuery(QUERY_KEYS.user.config(userId), () => fetchUserConfig(userId));

    const [errors, setErrors] = useState<Record<string, string[]>>({});
    const [user, setUser] = useState({} as User);
    const [userConfigState, setUserConfigState] = useState<UserConfig>({} as UserConfig);

    const {data: defaultUserConfig, isLoading: isLoadingConfig} = useQuery(QUERY_KEYS.user.defaultConfig(), () =>
        fetchDefaultUserConfigValues()
    );

    useEffect(() => {
        setUserConfigState(userConfig || defaultUserConfig || {});
    }, [userConfig, defaultUserConfig]);

    useEffect(() => {
        selectedUser && setUser(selectedUser);
    }, [selectedUser]);

    const updateMutation = useUpdateUserMutation({
        onSuccess: (data) => {
            if (data.success) {
                client.invalidateQueries({queryKey: QUERY_KEYS.user.detail(user._id)});
                client.invalidateQueries({queryKey: QUERY_KEYS.user.config(user._id)});
                client.invalidateQueries({queryKey: QUERY_KEYS.user.lists()});
                toast.success(`${t('toasts.userSuccessfullyUpdated')}`);
                onClose();
            }

            if (!data.success && data.errors) {
                toast.error(`${t(data.errors.message[0] as TypedTranslation)}`);
                setErrors(data.errors);
            }
        },
    });

    function updateSelectedUser(): void {
        const validation = updateUserValidator.safeParse(user);

        if (validation.success) {
            setErrors({});
            updateMutation.mutate({
                user: {
                    ...user,
                    isAnonymous: true,
                    adminId: (loggedInUser as User)._id,
                },
                userConfig: {...userConfigState},
            });
        } else {
            setErrors(transformZodErrors(validation.error.errors));
        }
    }

    return (
        <UserForm
            id={EDIT_USER_FORM_ID}
            type={UserFormType.UPDATE}
            user={user}
            setUser={setUser}
            userConfigState={userConfigState}
            setUserConfigState={setUserConfigState}
            errors={errors}
            loggedInUser={loggedInUser}
            spinner={isLoadingUser || isLoadingConfig || updateMutation.isLoading}
            onSubmit={updateSelectedUser}
        />
    );
}
